import { useEffect, useState, useRef, useMemo, Fragment } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Radio, RefreshCw, Loader2, Rewind, Play, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_TONE = {
  available: "#22C55E",
  on_trip: "#3B82F6",
  offline: "#94A3B8",
};

function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = points.map((p) => [p.lat, p.lng]);
    try { map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 }); } catch {}
  }, [points, map]);
  return null;
}

// Turn a full-vehicle string like "Mercedes E-Class" or "Toyota Camry Hybrid"
// into a stable filter chip label. We use the make so filters group nicely
// even when the fleet has multiple models per make.
const vehicleClass = (v) => {
  if (!v) return "Unclassified";
  return v.trim().split(/\s+/)[0]; // Mercedes / Toyota / Audi / …
};

export default function FleetHeatmap() {
  const [data, setData] = useState({ drivers: [], at: null });
  const [loading, setLoading] = useState(true);
  const [minutesAgo, setMinutesAgo] = useState(0);   // 0 = live now, up to 30
  // Filter state — statuses is a Set of statuses currently shown (toggleable);
  // vehicleFocus is a single-select "focus mode" for a vehicle class.
  const [statuses, setStatuses] = useState(new Set(["available", "on_trip", "offline"]));
  const [vehicleFocus, setVehicleFocus] = useState("all");
  const timerRef = useRef(null);

  const isReplay = minutesAgo > 0;

  const load = async () => {
    try {
      setLoading(true);
      const url = isReplay
        ? `/admin/fleet/replay?minutes_ago=${minutesAgo}`
        : "/admin/fleet/locations";
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fleet feed failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (isReplay) {
      // Freeze auto-refresh in replay mode so the slider owns the state.
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    } else {
      timerRef.current = setInterval(load, 10000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutesAgo]);

  const allDrivers = data.drivers || [];

  // Unique vehicle-class chips, sized off the current fleet.
  const vehicleClasses = useMemo(() => {
    const counts = new Map();
    for (const d of allDrivers) {
      const k = vehicleClass(d.vehicle);
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [allDrivers]);

  const drivers = useMemo(
    () => allDrivers.filter((d) => {
      if (!statuses.has(d.status)) return false;
      if (vehicleFocus !== "all" && vehicleClass(d.vehicle) !== vehicleFocus) return false;
      return true;
    }),
    [allDrivers, statuses, vehicleFocus]
  );

  const toggleStatus = (s) => {
    const next = new Set(statuses);
    if (next.has(s)) next.delete(s); else next.add(s);
    // Guard against empty state — one status must always be on so the map
    // doesn't go entirely blank on a mis-tap.
    if (next.size === 0) return;
    setStatuses(next);
  };

  const center = drivers.length
    ? [drivers[0].lat, drivers[0].lng]
    : [-36.8485, 174.7633]; // Auckland fallback

  return (
    <div data-testid="fleet-heatmap">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-caption mb-1">Live view</div>
          <h1 className="font-display text-3xl font-extrabold">Fleet heatmap</h1>
          <p className="text-slate-400 mt-1 text-sm">
            Red pulse = active jobs. Every driver reporting a GPS ping in the last 6h.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-500" data-testid="fleet-last-fetch">
            {data.at ? `Updated ${new Date(data.at).toLocaleTimeString()}` : "—"}
          </span>
          <Button size="sm" variant="outline" onClick={load} className="bg-transparent border-white/10 text-slate-300" data-testid="fleet-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Toggleable stat/filter chips — tap to hide, tap again to bring back */}
      <div className="grid md:grid-cols-4 gap-2 mb-3" data-testid="fleet-status-chips">
        <FilterChip color="emerald" label="Available" value={allDrivers.filter((d) => d.status === "available").length} active={statuses.has("available")} onClick={() => toggleStatus("available")} testid="filter-available" />
        <FilterChip color="blue" label="On trip" value={allDrivers.filter((d) => d.status === "on_trip").length} active={statuses.has("on_trip")} onClick={() => toggleStatus("on_trip")} testid="filter-on-trip" />
        <FilterChip color="slate" label="Offline" value={allDrivers.filter((d) => d.status === "offline").length} active={statuses.has("offline")} onClick={() => toggleStatus("offline")} testid="filter-offline" />
        <div className={`rounded-lg border px-3 py-2 bg-red-500/10 border-red-400/30 text-red-300`}>
          <div className="text-[10px] uppercase tracking-widest font-mono opacity-80">Active jobs</div>
          <div className="font-display font-bold text-2xl">{drivers.reduce((n, d) => n + (d.active_bookings || 0), 0)}</div>
        </div>
      </div>

      {/* Vehicle class focus — single-select pill row derived from the fleet */}
      {vehicleClasses.length > 1 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="fleet-vehicle-chips">
          <div className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-slate-500 mr-1">
            <Filter className="w-3 h-3" /> Focus
          </div>
          <VehiclePill label="All" count={allDrivers.length} active={vehicleFocus === "all"} onClick={() => setVehicleFocus("all")} testid="focus-all" />
          {vehicleClasses.map(([k, n]) => (
            <VehiclePill key={k} label={k} count={n} active={vehicleFocus === k} onClick={() => setVehicleFocus(k)} testid={`focus-${k.toLowerCase()}`} />
          ))}
        </div>
      )}

      {/* Replay time slider — scrub up to 30 minutes back */}
      <div className={`rounded-xl border p-3 mb-4 ${isReplay ? "bg-red-500/5 border-red-400/30" : "bg-black/20 border-white/10"}`} data-testid="fleet-replay-panel">
        <div className="flex items-center gap-3 mb-2">
          {isReplay ? <Rewind className="w-4 h-4 text-red-400" /> : <Play className="w-4 h-4 text-emerald-400" />}
          <div className="text-[11px] uppercase tracking-widest font-mono flex-1">
            {isReplay ? (
              <span className="text-red-300">
                Replay · <b>{minutesAgo} min ago</b>
                {data.target_at && <span className="ml-2 text-red-200/70">({new Date(data.target_at).toLocaleTimeString()})</span>}
                <span className="ml-2 text-red-200/50">· breadcrumb trails on</span>
              </span>
            ) : (
              <span className="text-emerald-300">Live · auto-refresh every 10s</span>
            )}
          </div>
          {isReplay && (
            <Button size="sm" onClick={() => setMinutesAgo(0)} className="btn-gold rounded-full" data-testid="fleet-replay-live">
              Back to live
            </Button>
          )}
        </div>
        <input
          type="range" min="0" max="30" step="1"
          value={minutesAgo}
          onChange={(e) => setMinutesAgo(Number(e.target.value))}
          className="w-full accent-red-500"
          data-testid="fleet-replay-slider"
        />
        <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-1">
          <span>-30 min</span><span>-20</span><span>-10</span><span>Now</span>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-white/10 relative" style={{ height: 560 }}>
        {loading && (
          <div className="absolute inset-0 z-[500] grid place-items-center bg-black/40 backdrop-blur-sm">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
          </div>
        )}
        <MapContainer center={center} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          />
          <AutoFit points={drivers} />
          {drivers.map((d) => {
            const isHot = (d.active_bookings || 0) > 0;
            const outer = isHot ? 22 + Math.min(d.active_bookings, 4) * 4 : 14;
            const fill = isHot ? "#DC2626" : STATUS_TONE[d.status] || "#94A3B8";
            // In replay mode the backend hands us a trail — draw a series of
            // fading polyline segments so the newest movement is bright and
            // older breadcrumbs dissolve into the background.
            const trail = Array.isArray(d.trail) ? d.trail : [];
            const trailSegments = trail.length >= 2
              ? trail.slice(0, -1).map((_, i) => ({
                  a: trail[i], b: trail[i + 1],
                  op: 0.18 + 0.62 * ((i + 1) / trail.length),
                }))
              : [];
            return (
              <Fragment key={d.id}>
                {trailSegments.map((s, i) => (
                  <Polyline
                    key={`trail-${d.id}-${i}`}
                    positions={[[s.a.lat, s.a.lng], [s.b.lat, s.b.lng]]}
                    pathOptions={{ color: fill, weight: 3, opacity: s.op, dashArray: i === 0 ? "3 4" : null }}
                  />
                ))}
                {isHot && (
                  <CircleMarker
                    center={[d.lat, d.lng]}
                    radius={outer}
                    pathOptions={{
                      color: "#DC2626", weight: 1, opacity: 0.35,
                      fillColor: "#DC2626", fillOpacity: 0.15,
                      className: "fleet-pulse",
                    }}
                    interactive={false}
                  />
                )}
                <CircleMarker
                  center={[d.lat, d.lng]}
                  radius={isHot ? 9 : 7}
                  pathOptions={{ color: "#FFFFFF", weight: 2, fillColor: fill, fillOpacity: 0.95 }}
                >
                  <Popup>
                    <div style={{ fontFamily: "Outfit, sans-serif" }}>
                      <div style={{ fontWeight: 700 }}>{d.name}</div>
                      <div style={{ fontSize: 12, color: "#555" }}>{d.vehicle} · {d.plate}</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        Status: <b>{d.status.replace("_", " ")}</b><br />
                        Active jobs: <b style={{ color: isHot ? "#DC2626" : "#333" }}>{d.active_bookings || 0}</b><br />
                        {d.updated_at && (<>Last ping: {new Date(d.updated_at).toLocaleTimeString()}</>)}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              </Fragment>
            );
          })}
        </MapContainer>
      </div>

      {drivers.length === 0 && !loading && (
        <div className="mt-4 glass-card rounded-xl p-6 text-center text-slate-500 gold-border" data-testid="fleet-empty">
          <Radio className="w-6 h-6 mx-auto text-amber-400/60 mb-2" />
          {allDrivers.length === 0
            ? <>No drivers reporting GPS. Ask them to tap <b>Go Live</b> in the driver app.</>
            : <>No drivers match the current filter. <button onClick={() => { setStatuses(new Set(["available","on_trip","offline"])); setVehicleFocus("all"); }} className="text-red-300 hover:text-red-200 underline underline-offset-2 ml-1" data-testid="fleet-clear-filters">Clear filters</button></>}
        </div>
      )}

      <style>{`
        @keyframes fleet-pulse-kf {
          0%   { transform: scale(0.85); opacity: 0.6; }
          70%  { transform: scale(1.4);  opacity: 0.05; }
          100% { transform: scale(0.85); opacity: 0.6; }
        }
        .fleet-pulse { animation: fleet-pulse-kf 1.8s ease-out infinite; transform-origin: center; transform-box: fill-box; }
      `}</style>
    </div>
  );
}

const CHIP_TONE = {
  emerald: { on: "bg-emerald-500/10 border-emerald-400/30 text-emerald-300", off: "bg-white/[0.02] border-white/10 text-slate-500" },
  blue:    { on: "bg-blue-500/10 border-blue-400/30 text-blue-300", off: "bg-white/[0.02] border-white/10 text-slate-500" },
  red:     { on: "bg-red-500/10 border-red-400/30 text-red-300", off: "bg-white/[0.02] border-white/10 text-slate-500" },
  slate:   { on: "bg-white/5 border-white/10 text-slate-300", off: "bg-white/[0.02] border-white/10 text-slate-600" },
};

const FilterChip = ({ color, label, value, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`rounded-lg border px-3 py-2 text-left transition-colors hover:brightness-110 ${
      active ? CHIP_TONE[color].on : CHIP_TONE[color].off + " opacity-60 line-through"
    }`}
  >
    <div className="text-[10px] uppercase tracking-widest font-mono opacity-80">{label}</div>
    <div className="font-display font-bold text-2xl">{value}</div>
  </button>
);

const VehiclePill = ({ label, count, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-mono uppercase tracking-wider transition-colors ${
      active
        ? "bg-red-500 border-red-400 text-white"
        : "bg-black/20 border-white/10 text-slate-400 hover:text-white hover:border-white/30"
    }`}
  >
    {label} <span className="opacity-70">·{count}</span>
  </button>
);
